import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Alert,
    Chip, IconButton, Tooltip, InputAdornment, Pagination, Stack, LinearProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { dayStartISO, dayEndISO } from '../../../../utils/dateRange';
import { VOUCHER_SHEET, buildVoucherRow } from '../../../../config/voucherColumns';
import { HPV_VOUCHER_SHEET } from '../../../../config/hpvVoucherColumns';
import { toast } from 'react-toastify';

const PAGE_SIZES = [25, 50, 100];

// The Ministry workbooks rows can be copied into: the general voucher sheet and
// the HPV programme's own.
const SHEETS = [VOUCHER_SHEET, HPV_VOUCHER_SHEET];

// Tab-separated block (the plain-text clipboard fallback). Tabs/newlines inside a
// cell would break the grid, so they're squashed to spaces.
const toTSV = (rows) =>
    rows.map((r) => r.map((c) => String(c ?? '').replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n');

const escapeHtml = (s) => String(s ?? '')
    .replace(/[\t\r\n]+/g, ' ')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Excel and Google Sheets both honor `mso-number-format` on pasted HTML cells.
// Force TEXT ('\@') for anything that isn't a genuine number so phone numbers,
// National IDs, voucher serials and dd/mm/yyyy dates paste literally instead of
// being mangled into dates / scientific notation. Genuine numeric cells (age,
// numeric results — the columns tagged `num`) paste as numbers. Using an HTML
// table (not just TSV) also guarantees column alignment regardless of content.
const cellHtml = (value, col) => {
    const numeric = col && col.num && /^-?\d+(?:\.\d+)?$/.test(String(value).trim());
    const fmt = numeric ? 'General' : '\\@';
    return `<td style="mso-number-format:'${fmt}'">${escapeHtml(value)}</td>`;
};

const buildHtml = (sheet, rows, withHeader) => {
    const header = withHeader
        ? `<tr>${sheet.headers.map((h) => `<td style="mso-number-format:'\\@'">${escapeHtml(h)}</td>`).join('')}</tr>`
        : '';
    const body = rows.map((cells) => `<tr>${cells.map((c, i) => cellHtml(c, sheet.columns[i])).join('')}</tr>`).join('');
    return `<table>${header}${body}</table>`;
};

async function writeClipboard(tsv, html) {
    // Preferred: write both HTML (typed cells) and plain text (fallback) so the
    // paste target picks the richest it supports. Browsers offer this API only
    // over HTTPS; on plain HTTP the tab-separated text is copied instead, which
    // still pastes into the right columns.
    try {
        if (navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([
                new window.ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([tsv], { type: 'text/plain' }),
                }),
            ]);
            return true;
        }
    } catch (e) { /* fall through to plain text */ }
    try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(tsv); return true; }
    } catch (e) { /* fall through to legacy */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = tsv; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch (e) {
        return false;
    }
}

// "YYYY-MM-DD" in the viewer's local time, and the quick ranges offered above
// the dates. A month is the usual submission batch, so the tab opens on this one.
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const RANGES = {
    today: () => { const t = new Date(); return [ymd(t), ymd(t)]; },
    week: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - 6); return [ymd(s), ymd(t)]; },
    month: () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth(), 1)), ymd(t)]; },
    lastMonth: () => { const t = new Date(); return [ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), ymd(new Date(t.getFullYear(), t.getMonth(), 0))]; },
};

// Left-pinned columns (checkbox + patient identifier) so the row you're on stays
// visible while the data columns scroll horizontally. z-index layering:
// pinned-header (6) > pinned-body (3) > scrolling-header (2, MUI default) > body.
const pinCheckbox = { position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' };
const pinPatient = { position: 'sticky', left: 46, zIndex: 3, bgcolor: 'background.paper', minWidth: 230, borderRight: '2px solid', borderColor: 'divider' };
const pinCheckboxHead = { ...pinCheckbox, zIndex: 6 };
const pinPatientHead = { ...pinPatient, zIndex: 6 };

// One grid row. The data cells are plain <td>s and the row re-renders only
// when its own selection or copied mark changes: typing in the filter or ticking
// one row no longer redraws thousands of styled cells.
const VoucherRow = memo(function VoucherRow({ row, isSelected, isCopied, onToggle, onCopy }) {
    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox" sx={pinCheckbox}>
                <Checkbox size="small" checked={isSelected} onChange={() => onToggle(row.id)} inputProps={{ 'aria-label': `Select ${row.label}` }} />
            </TableCell>
            <TableCell sx={pinPatient}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Copy this row">
                        <IconButton size="small" onClick={() => onCopy(row)}>
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                    <span>{row.label}</span>
                    {!row.hasId && (
                        <Tooltip title="No National ID or passport number on the patient record">
                            <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} aria-label="No ID" />
                        </Tooltip>
                    )}
                    {isCopied && <Chip size="small" color="success" variant="outlined" icon={<CheckIcon />} label="Copied" sx={{ height: 20 }} />}
                </Box>
            </TableCell>
            {row.cells.map((cell, i) => <td key={i}>{cell}</td>)}
        </TableRow>
    );
});

export default function VoucherExport() {
    const { currentBranch } = useSelector((state) => state.branch);
    const hospitalConfig = useSelector((state) => state.hospitalConfig);
    const enabled = !!hospitalConfig?.features?.['lis.voucherSubmission'];
    const providerName = hospitalConfig?.name || hospitalConfig?.branding?.displayName || '';
    const tz = hospitalConfig?.localization?.timezone;

    const [sheetKey, setSheetKey] = useState(VOUCHER_SHEET.key);
    const sheet = SHEETS.find((s) => s.key === sheetKey) || VOUCHER_SHEET;
    const [statusFilter, setStatusFilter] = useState('verified');
    const [range, setRange] = useState('month');
    const [dateFrom, setDateFrom] = useState(() => RANGES.month()[0]);
    const [dateTo, setDateTo] = useState(() => RANGES.month()[1]);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [selected, setSelected] = useState(() => new Set()); // row ids
    const [copied, setCopied] = useState(() => new Set()); // row ids copied since this list was loaded
    const [includeHeader, setIncludeHeader] = useState(false);
    const [quickFilter, setQuickFilter] = useState('');
    const [testFilter, setTestFilter] = useState(''); // testCode, '' = all
    const [voucherOnly, setVoucherOnly] = useState(false);
    const [hideCopied, setHideCopied] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const requestSeq = useRef(0);
    // Typing stays responsive: the grid catches up with the filter text after.
    const deferredQuery = useDeferredValue(quickFilter);
    const badRange = !!dateFrom && !!dateTo && dateFrom > dateTo;

    // Map each fetched record that belongs on the chosen sheet to its row of
    // cells, its test codes (Test filter), whether it carries a government
    // voucher serial (Voucher-only filter), and a lowercased search blob
    // (patient, ID, serial, invoice, order, tests). Ids are per sheet, so a row
    // copied into one workbook isn't marked copied for the other.
    const displayRows = useMemo(
        () => records.filter(sheet.includes).map((rec) => {
            const p = rec.patient || {};
            const tests = rec.results || [];
            const testCodes = tests.map((r) => r.testCode).filter(Boolean);
            const search = [
                p.fName, p.lName, p.nationalId, rec.invoice?.voucherSerialNo,
                rec.invoice?.invoiceNumber, rec.labOrderId,
                ...testCodes, ...tests.map((r) => r.name),
            ].filter(Boolean).join(' ').toLowerCase();
            return {
                id: `${sheet.key}:${rec.orderId}`,
                label: `${rec.labOrderId || ''} · ${(p.fName || '')} ${(p.lName || '')}`.trim(),
                search,
                testCodes,
                hasId: !!String(p.nationalId || '').trim(),
                hasVoucher: !!String(rec.invoice?.voucherSerialNo || '').trim(),
                cells: buildVoucherRow(rec, { providerName, tz }, sheet.columns),
            };
        }),
        [records, sheet, providerName, tz]
    );

    // Distinct tests on the sheet's orders — populates the Test dropdown.
    const availableTests = useMemo(() => {
        const map = new Map();
        records.filter(sheet.includes).forEach((rec) => (rec.results || []).forEach((r) => {
            if (r.testCode && !map.has(r.testCode)) map.set(r.testCode, r.name || r.testCode);
        }));
        return [...map.entries()].map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [records, sheet]);

    // Client-side filters over the loaded rows — narrow what's shown (and what
    // "Copy All"/"select-all" act on) without re-fetching.
    const filteredRows = useMemo(() => {
        const q = deferredQuery.trim().toLowerCase();
        return displayRows.filter((r) =>
            (!q || r.search.includes(q)) &&
            (!testFilter || r.testCodes.includes(testFilter)) &&
            (!voucherOnly || r.hasVoucher) &&
            (!hideCopied || !copied.has(r.id))
        );
    }, [displayRows, deferredQuery, testFilter, voucherOnly, hideCopied, copied]);

    // Back to the first page whenever the sheet, the filters or the loaded set change.
    useEffect(() => { setPage(1); }, [sheetKey, deferredQuery, testFilter, voucherOnly, hideCopied, records, pageSize]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);
    const missingId = useMemo(() => displayRows.filter((r) => !r.hasId).length, [displayRows]);

    const fetchRows = useCallback(async ({ from = dateFrom, to = dateTo } = {}) => {
        if (!currentBranch?.id) return;
        if (from && to && from > to) { toast.info('The From date is after the To date'); return; }
        const seq = ++requestSeq.current;
        try {
            setLoading(true);
            const token = localStorage.getItem('item');
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (from) params.set('dateFrom', dayStartISO(from));
            if (to) params.set('dateTo', dayEndISO(to));
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_API}api/laboratory/reports/voucher-rows?${params.toString()}`,
                { method: 'GET', headers: { Authorization: `Bearer ${token}`, 'X-Current-Branch': currentBranch.id } }
            );
            const json = await response.json();
            if (seq !== requestSeq.current) return; // a newer load (or a branch switch) took over
            if (!response.ok || !json?.success) {
                toast.error(json?.message || 'Failed to load voucher rows');
                return;
            }
            setRecords(json.data || []);
            setSelected(new Set());
            setCopied(new Set());
            setTestFilter('');
            setLoaded(true);
        } catch (e) {
            if (seq === requestSeq.current) toast.error('Failed to load voucher rows');
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [currentBranch?.id, statusFilter, dateFrom, dateTo]);

    // Opens on this month for the selected branch; switching branch clears the
    // other branch's rows and loads this one's.
    const fetchRef = useRef(fetchRows);
    fetchRef.current = fetchRows;
    useEffect(() => {
        if (!enabled || !currentBranch?.id) return;
        setRecords([]);
        setLoaded(false);
        fetchRef.current();
    }, [enabled, currentBranch?.id]);

    const pickRange = (key) => {
        if (!key) return;
        const [from, to] = RANGES[key]();
        setRange(key);
        setDateFrom(from);
        setDateTo(to);
        fetchRows({ from, to });
    };

    const shownIds = useMemo(() => filteredRows.map((r) => r.id), [filteredRows]);
    const allSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
    const someSelected = shownIds.some((id) => selected.has(id));
    const toggleAll = () => setSelected((prev) => {
        const next = new Set(prev);
        if (allSelected) shownIds.forEach((id) => next.delete(id));
        else shownIds.forEach((id) => next.add(id));
        return next;
    });
    const toggleOne = useCallback((id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }), []);

    const includeHeaderRef = useRef(includeHeader);
    includeHeaderRef.current = includeHeader;
    const copyRows = useCallback(async (rowsList, verb) => {
        if (rowsList.length === 0) { toast.info('Nothing to copy'); return; }
        const body = rowsList.map((r) => r.cells);
        const withHeader = includeHeaderRef.current;
        const tsvRows = withHeader ? [sheet.headers, ...body] : body;
        const ok = await writeClipboard(toTSV(tsvRows), buildHtml(sheet, body, withHeader));
        if (ok) {
            setCopied((prev) => { const next = new Set(prev); rowsList.forEach((r) => next.add(r.id)); return next; });
            toast.success(`Copied ${body.length} ${verb} — paste into the submission sheet`);
        } else {
            toast.error('Copy failed — your browser blocked clipboard access');
        }
    }, [sheet]);

    const copyAll = () => copyRows(filteredRows, `row${filteredRows.length > 1 ? 's' : ''}`);
    const copySelected = () => copyRows(filteredRows.filter((r) => selected.has(r.id)), 'selected');
    const copyOne = useCallback((row) => copyRows([row], 'row'), [copyRows]);

    if (!enabled) {
        return (
            <Alert severity="info">
                Voucher Submission isn’t enabled for this deployment. Set{' '}
                <code>features['lis.voucherSubmission']</code> in the hospital config to turn it on.
            </Alert>
        );
    }

    const selectedShown = shownIds.filter((id) => selected.has(id)).length;
    const filtered = filteredRows.length !== displayRows.length;
    const copiedCount = displayRows.reduce((n, r) => n + (copied.has(r.id) ? 1 : 0), 0);
    const hasGroups = sheet.groups.some((g) => g.label);
    const headTop = hasGroups ? 30 : 0; // the column headers stick below the group row

    return (
        <Box sx={{ maxWidth: '100%' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
                Finalized orders mapped to the government voucher spreadsheet’s columns. Pick the period, optionally
                filter, then <strong>Copy</strong> — cells paste into the right columns of your submission Google Sheet.
                Copied rows are ticked so nothing is pasted twice.
            </Alert>

            {/* Server fetch scope */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup size="small" exclusive value={range} onChange={(e, v) => pickRange(v)} aria-label="Period">
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="week">Last 7 days</ToggleButton>
                    <ToggleButton value="month">This month</ToggleButton>
                    <ToggleButton value="lastMonth">Last month</ToggleButton>
                </ToggleButtonGroup>
                <TextField label="From" type="date" size="small" value={dateFrom} error={badRange}
                    onChange={(e) => { setDateFrom(e.target.value); setRange(''); }} InputLabelProps={{ shrink: true }} />
                <TextField label="To" type="date" size="small" value={dateTo} error={badRange}
                    onChange={(e) => { setDateTo(e.target.value); setRange(''); }} InputLabelProps={{ shrink: true }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="verified">Verified (Ready)</MenuItem>
                        <MenuItem value="released">Released</MenuItem>
                        <MenuItem value="resulted">Resulted</MenuItem>
                        <MenuItem value="">Any status</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => fetchRows()} disabled={loading || badRange}>
                    {loading ? 'Loading…' : 'Load'}
                </Button>
                {badRange && <Typography variant="caption" color="text.secondary">The From date is after the To date.</Typography>}
            </Box>

            {/* Sheet + filter + copy actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup size="small" exclusive value={sheetKey} aria-label="Submission sheet"
                    onChange={(e, v) => { if (v) { setSheetKey(v); setTestFilter(''); } }}>
                    {SHEETS.map((s) => <ToggleButton key={s.key} value={s.key}>{s.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <TextField
                    size="small" placeholder="Filter by name, ID, serial, invoice, test…"
                    value={quickFilter} onChange={(e) => setQuickFilter(e.target.value)}
                    sx={{ minWidth: 300 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Test</InputLabel>
                    <Select value={testFilter} label="Test" onChange={(e) => setTestFilter(e.target.value)}>
                        <MenuItem value="">All tests</MenuItem>
                        {availableTests.map((t) => (
                            <MenuItem key={t.code} value={t.code}>{t.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Checkbox size="small" checked={voucherOnly} onChange={(e) => setVoucherOnly(e.target.checked)} />}
                    label="Voucher only"
                />
                <FormControlLabel
                    control={<Checkbox size="small" checked={hideCopied} onChange={(e) => setHideCopied(e.target.checked)} />}
                    label={`Hide copied${copiedCount ? ` (${copiedCount})` : ''}`}
                />
                <Button variant="contained" color="success" startIcon={<ContentCopyIcon />}
                    onClick={copyAll} disabled={filteredRows.length === 0}>
                    Copy All{filteredRows.length ? ` (${filteredRows.length})` : ''}
                </Button>
                <Button variant="outlined" startIcon={<ContentCopyIcon />}
                    onClick={copySelected} disabled={selectedShown === 0}>
                    Copy Selected{selectedShown ? ` (${selectedShown})` : ''}
                </Button>
                <FormControlLabel
                    control={<Checkbox size="small" checked={includeHeader} onChange={(e) => setIncludeHeader(e.target.checked)} />}
                    label="Include header row"
                />
                {loaded && (
                    <Chip size="small" variant="outlined"
                        label={filtered ? `${filteredRows.length} shown / ${displayRows.length} loaded` : `${displayRows.length} order${displayRows.length === 1 ? '' : 's'}`} />
                )}
                {loaded && missingId > 0 && (
                    <Chip size="small" color="warning" variant="outlined" icon={<WarningAmberIcon />}
                        label={`${missingId} without an ID / passport number`} />
                )}
            </Box>

            {sheet.note && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: -1, mb: 1.5 }}>
                    {sheet.note}
                </Typography>
            )}

            {/* The sheet's grid — scrolls both ways inside its own box; header +
                first two columns stay pinned. */}
            {loading && <LinearProgress sx={{ mb: 0.5 }} />}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '68vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{
                    '& td, & th': { whiteSpace: 'nowrap', fontSize: 12, py: 0.5 },
                    '& tbody td:not(.MuiTableCell-root)': { px: 1, borderBottom: '1px solid', borderColor: 'divider' },
                }}>
                    <TableHead>
                        {/* Section-group row (CBC, Lipid Profile, …), for a sheet that has them */}
                        {hasGroups && (
                            <TableRow>
                                <TableCell padding="checkbox" sx={pinCheckboxHead} />
                                <TableCell sx={pinPatientHead}>Patient / Order</TableCell>
                                {sheet.groups.map((g, i) => (
                                    <TableCell key={i} colSpan={g.span} align="center"
                                        sx={{ fontWeight: 700, bgcolor: g.label ? 'primary.light' : 'background.paper', color: g.label ? 'primary.contrastText' : 'inherit', borderLeft: '1px solid', borderColor: 'divider' }}>
                                        {g.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                        {/* Column-header row (the sheet's headers) */}
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ ...pinCheckboxHead, top: headTop }}>
                                <Checkbox size="small" checked={allSelected} indeterminate={someSelected && !allSelected}
                                    onChange={toggleAll} disabled={shownIds.length === 0} inputProps={{ 'aria-label': 'Select all shown' }} />
                            </TableCell>
                            <TableCell sx={{ ...pinPatientHead, top: headTop, fontWeight: 600 }}>Copy row</TableCell>
                            {sheet.columns.map((c, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, top: headTop }}>{c.header}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row) => (
                            <VoucherRow key={row.id} row={row}
                                isSelected={selected.has(row.id)} isCopied={copied.has(row.id)}
                                onToggle={toggleOne} onCopy={copyOne} />
                        ))}
                        {filteredRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={sheet.columns.length + 2} sx={{ py: 4, color: 'text.secondary' }}>
                                    {loading ? 'Loading…'
                                        : loaded
                                            ? (filtered ? 'No rows match the filters.' : sheet.emptyText)
                                            : 'Pick a period to load.'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredRows.length > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length}
                        </Typography>
                        <FormControl size="small" variant="standard">
                            <Select value={pageSize} onChange={(e) => setPageSize(e.target.value)} inputProps={{ 'aria-label': 'Rows per page' }}>
                                {PAGE_SIZES.map((n) => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                    {pageCount > 1 && (
                        <Pagination count={pageCount} page={page} onChange={(e, v) => setPage(v)} color="primary" size="small" />
                    )}
                </Stack>
            )}
        </Box>
    );
}
